import { useState, useEffect } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';

interface Announcement {
  id: string;
  title: string;
  content: string | null;
  type: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

const EDGE_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/announcements-admin';

const TYPE_OPTIONS = [
  { value: 'info', label: '普通通知', icon: 'ri-megaphone-line', color: 'text-emerald-600 bg-emerald-50' },
  { value: 'promo', label: '促销活动', icon: 'ri-gift-line', color: 'text-amber-600 bg-amber-50' },
  { value: 'warning', label: '重要提醒', icon: 'ri-alarm-warning-line', color: 'text-rose-600 bg-rose-50' },
];

const getTypeLabel = (type: string) => TYPE_OPTIONS.find((t) => t.value === type) ?? TYPE_OPTIONS[0];

// ── 新增/编辑弹窗 ─────────────────────────────────────────────────────────────
interface EditModalProps {
  item: Partial<Announcement> | null;
  onClose: () => void;
  onSaved: () => void;
}

const EditModal = ({ item, onClose, onSaved }: EditModalProps) => {
  const isEdit = !!item?.id;
  const [title, setTitle] = useState(item?.title ?? '');
  const [type, setType] = useState(item?.type ?? 'info');
  const [sortOrder, setSortOrder] = useState(item?.sort_order ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!title.trim()) { setError('请输入公告标题'); return; }
    setSaving(true); setError('');
    try {
      const payload = { title: title.trim(), type, sort_order: sortOrder };
      let res: Response;
      if (isEdit) {
        res = await fetch(EDGE_URL, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item!.id, ...payload }),
        });
      } else {
        res = await fetch(EDGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const json = await res.json();
      if (!res.ok || json.error) { setError('保存失败，请重试'); return; }
      onSaved();
    } catch {
      setError('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !saving && onClose()} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl w-full max-w-md p-6"
          style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-gray-900 text-base">{isEdit ? '编辑公告' : '新增公告'}</h3>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                公告标题 <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setError(''); }}
                placeholder="请输入公告内容（将在首页滚动展示）"
                maxLength={100}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-emerald-400"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/100</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">公告类型</label>
              <div className="flex gap-2">
                {TYPE_OPTIONS.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setType(t.value)}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 cursor-pointer transition-all ${type === t.value ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${t.color}`}>
                      <i className={`${t.icon} text-base`} />
                    </div>
                    <span className="text-xs text-gray-600">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">排序权重</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                min={0}
                max={999}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-emerald-400"
              />
              <p className="text-xs text-gray-400 mt-1">数字越小越靠前展示</p>
            </div>
          </div>

          {error && (
            <div className="mt-4 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl">
              <i className="ri-error-warning-line flex-shrink-0" />{error}
            </div>
          )}

          <div className="flex gap-3 mt-5">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !title.trim()}
              className="flex-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 disabled:opacity-50"
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ── 主页面 ────────────────────────────────────────────────────────────────────
const AdminAnnouncementsPage = () => {
  const [list, setList] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<Partial<Announcement> | null | undefined>(undefined);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(EDGE_URL);
      const json = await res.json();
      setList((json.data as Announcement[]) ?? []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const toggleActive = async (item: Announcement) => {
    if (togglingId === item.id) return;
    const newValue = !item.is_active;
    setTogglingId(item.id);
    try {
      const res = await fetch(EDGE_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, is_active: newValue }),
      });
      const json = await res.json();
      if (res.ok && !json.error) {
        // 数据库确认成功后再更新 UI
        setList((prev) => prev.map((a) => a.id === item.id ? { ...a, is_active: newValue } : a));
      }
    } catch {
      // 失败不更新 UI，保持原状
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await fetch(EDGE_URL, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      setList((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AdminLayout title="公告管理">
      <div className="max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-sm text-gray-500">管理首页公告栏内容，启用的公告将在首页滚动展示</p>
          </div>
          <button
            onClick={() => setEditItem({})}
            className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors"
          >
            <i className="ri-add-line" />
            新增公告
          </button>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <span className="w-5 h-5 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin mr-2" />
              加载中...
            </div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mb-3">
                <i className="ri-megaphone-line text-2xl" />
              </div>
              <p className="text-sm">暂无公告，点击右上角新增</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">公告内容</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-3 py-3 w-24">类型</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-3 py-3 w-16">排序</th>
                  <th className="text-left text-xs font-medium text-gray-400 px-3 py-3 w-20">状态</th>
                  <th className="text-right text-xs font-medium text-gray-400 px-5 py-3 w-28">操作</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, i) => {
                  const typeCfg = getTypeLabel(item.type);
                  const isToggling = togglingId === item.id;
                  return (
                    <tr
                      key={item.id}
                      className={`${i < list.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}
                    >
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-gray-800 font-medium">{item.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{new Date(item.created_at).toLocaleDateString('zh-CN')}</p>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${typeCfg.color}`}>
                          <i className={typeCfg.icon} />
                          {typeCfg.label}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className="text-sm text-gray-600">{item.sort_order}</span>
                      </td>
                      <td className="px-3 py-3.5">
                        <button
                          onClick={() => toggleActive(item)}
                          disabled={isToggling}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer disabled:opacity-60 ${item.is_active ? 'bg-emerald-500' : 'bg-gray-200'}`}
                        >
                          {isToggling ? (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            </span>
                          ) : (
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${item.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                          )}
                        </button>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditItem(item)}
                            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg cursor-pointer transition-colors"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={deletingId === item.id}
                            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg cursor-pointer transition-colors disabled:opacity-50"
                          >
                            {deletingId === item.id
                              ? <span className="w-3.5 h-3.5 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
                              : <i className="ri-delete-bin-line text-sm" />
                            }
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editItem !== undefined && (
        <EditModal
          item={editItem}
          onClose={() => setEditItem(undefined)}
          onSaved={() => { setEditItem(undefined); fetchList(); }}
        />
      )}

      <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
    </AdminLayout>
  );
};

export default AdminAnnouncementsPage;
